/**
 * Laedt Custom-Element-Module erst, wenn das Element wirklich im Dokument steht.
 *
 * ⚠️ Warum ueberhaupt: Gemessen registrierten rund 32–36 % des Theme-JS auf
 * jeder Seite Elemente, die dort nie vorkommen. Eine Bedingung nach Seitentyp
 * (`template.name == 'cart'`) waere die naheliegende Loesung — und falsch:
 *
 *   - Die Warenkorb-Schublade laesst sich auf JEDER Seite oeffnen. Ihr Inhalt
 *     wird per Section Rendering nachgeladen, `cart-discount-component` und
 *     `cart-quantity-selector-component` erscheinen also erst dann im DOM.
 *   - Der Auftraggeber kann im Customizer jederzeit einen Abschnitt mit Video
 *     oder Akkordeon auf eine beliebige Seite setzen.
 *
 * Beides wuerde eine statische Bedingung still brechen. Der Beobachter hier
 * nicht: er reagiert auf das, was tatsaechlich da ist, egal wann es kommt.
 *
 * Custom Elements duerfen spaeter definiert werden — der Browser wertet
 * passende Elemente dann nachtraeglich auf. Das kostet einen Wimpernschlag,
 * betrifft aber nur Verhalten, nicht Darstellung: die Module hier bringen
 * kein eigenes Layout mit.
 *
 * Vorbild im Theme: popover-polyfill-loader.js laedt nach demselben Muster.
 */

const script = document.currentScript;
/** @type {Record<string, string>} Elementname -> Moduladresse */
const karte = JSON.parse(script?.dataset.module ?? '{}');

/** Schon angestossene Module, damit nichts doppelt geladen wird. */
const geladen = new Set();

/**
 * ⚠️ Bewusst ein <script type="module">-Element statt `import(adresse)`.
 *
 * Der dynamische Import scheiterte an CORS: die Theme-Dateien liegen auf der
 * Shopify-CDN, also auf einer anderen Herkunft, und `import()` verlangt dafuer
 * CORS-Kopfzeilen. Gemessen brach damit accordion-custom.js auf der
 * Kollektionsseite weg — das Akkordeon waere tot gewesen.
 *
 * Ein eingehaengtes Modul-Script laedt genauso, wie das Theme seine uebrigen
 * rund vierzig Module laedt, und faellt deshalb nicht auf.
 *
 * @param {string} name
 */
function laden(name) {
  const adresse = karte[name];
  if (!adresse || geladen.has(name)) return;
  geladen.add(name);

  const el = document.createElement('script');
  el.type = 'module';
  el.src = adresse;
  el.addEventListener('error', () => {
    // Faellt das Nachladen aus, bleibt das Element unaufgewertet. Das Theme
    // ist ohne JavaScript bedienbar (siehe fuchs-konfigurator.js), deshalb
    // ist das ein Verlust an Komfort, kein Ausfall.
    geladen.delete(name);
  });
  document.head.append(el);
}

/** Prueft einen Teilbaum auf bekannte Elemente. @param {Element|Document} wurzel */
function pruefen(wurzel) {
  for (const name of Object.keys(karte)) {
    if (geladen.has(name)) continue;
    if (wurzel instanceof Element && wurzel.matches(name)) {
      laden(name);
      continue;
    }
    if (wurzel.querySelector(name)) laden(name);
  }
}

pruefen(document);

// Alles Nachgereichte im Blick behalten: Schubladeninhalt, Quick-Add-Modal,
// Section Rendering, Morph beim Seitenwechsel.
const beobachter = new MutationObserver((eintraege) => {
  // Sind alle Module angestossen, hat der Beobachter nichts mehr zu tun.
  if (geladen.size === Object.keys(karte).length) {
    beobachter.disconnect();
    return;
  }
  for (const eintrag of eintraege) {
    for (const knoten of eintrag.addedNodes) {
      if (knoten instanceof Element) pruefen(knoten);
    }
  }
});

beobachter.observe(document.documentElement, { childList: true, subtree: true });
